import { useState } from "react";
import { cn } from "./_utils";

type AvatarProps = React.HTMLAttributes<HTMLSpanElement> & {
  src?: string;
  alt?: string;
  /** Fallback rendered when there is no image or if it fails. */
  fallback: React.ReactNode;
};

/**
 * Avatar image with a text fallback when no image is available.
 *
 * @example
 * <Avatar src="/me.png" alt="Jane" fallback="JD" />
 * <Avatar fallback="?" />
 */
export function Avatar({
  className,
  src,
  alt,
  fallback,
  ...props
}: AvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(src) && !imageFailed;

  return (
    <span
      className={cn(
        "relative flex h-8 w-8 shrink-0 overflow-hidden rounded-full " +
          "bg-[var(--border-subtle)] text-text-primary",
        className
      )}
      {...props}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          className="aspect-square h-full w-full object-cover"
          // Google profile image URLs (lh3.googleusercontent.com) 403 if the
          // browser sends a cross-origin Referer; `no-referrer` unblocks them
          // without affecting same-origin avatar sources.
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
        />
      ) : null}
      {!showImage ? (
        <span
          className={cn(
            "flex h-full w-full items-center justify-center " +
              "text-xs font-medium text-text-secondary"
          )}
        >
          {fallback}
        </span>
      ) : null}
    </span>
  );
}
